import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Scissors, MailCheck } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("form"); // form|sent
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [delivery, setDelivery] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/auth/password-reset/request", { email });
      setDelivery(data?.delivery || "");
      setState("sent");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[#FAFAFA] text-[#0A0A0A]" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <Scissors size={20} className="text-[#0A0A0A]" strokeWidth={1.5}/>
          <div className="font-serif text-xl tracking-widest">MMS HUB CENTER</div>
        </div>
        <div className="border border-[#E5E5E5] p-8 bg-[#FFFFFF]">
          {state === "form" && (
            <form onSubmit={submit} data-testid="forgot-form">
              <div className="label-caps text-[#0A0A0A] mb-3">Forgot password</div>
              <h2 className="font-serif text-3xl font-light mb-6">Reset your password</h2>
              <p className="text-sm text-[#525252] mb-6">
                Enter the email associated with your account and we&#39;ll send you a secure link to set a new password.
              </p>

              <label className="label-caps block mb-2">Email</label>
              <input
                data-testid="forgot-email"
                type="email" required autoFocus
                value={email} onChange={(e)=>setEmail(e.target.value)}
                className="input-dark mb-5"
              />

              {err && <div className="text-sm text-red-600 mb-4" data-testid="forgot-err">{err}</div>}

              <button
                data-testid="forgot-submit"
                disabled={loading}
                className="btn-gold w-full disabled:opacity-50"
              >
                {loading ? "Sending…" : "Send reset link"}
              </button>
              <div className="text-center text-xs text-[#737373] mt-6">
                Remembered? <Link to="/login" className="text-[#0A0A0A]" data-testid="forgot-back-to-login">Sign in</Link>
              </div>
            </form>
          )}

          {state === "sent" && (
            <div data-testid="forgot-sent">
              <MailCheck size={28} className="text-[#0A0A0A] mb-4" strokeWidth={1.5}/>
              <div className="label-caps text-[#0A0A0A] mb-3">Check your email</div>
              <h2 className="font-serif text-3xl font-light mb-4">Link sent</h2>
              <p className="text-sm text-[#525252] leading-relaxed mb-4">
                If an account exists for <span className="text-[#0A0A0A]">{email}</span>, a reset link has been sent.
                The link expires in 60 minutes.
              </p>
              {delivery === "pending" && (
                <div className="text-xs text-[#171717] border border-[#E5E5E5] p-3 mb-4" data-testid="forgot-pending">
                  Email delivery is pending — the Resend integration hasn&#39;t been configured yet.
                </div>
              )}
              <Link to="/login" className="text-xs text-[#0A0A0A] hover:text-[#171717] uppercase tracking-widest" data-testid="forgot-back-link">Back to sign in</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
